import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  formatDurationMs,
  getJobRun,
  listJobRuns,
  listJobs,
  statusColor,
  triggerJob,
  updateJobSchedule,
  type JobDefinition,
  type JobRun,
} from '../services/jobsApi';
import JobScheduleEditor from '../components/jobs/JobScheduleEditor';
import {
  buildCronFromConfig,
  defaultScheduleConfig,
  describeSchedule,
  normalizeJobTimezone,
  validateScheduleConfig,
  type ScheduleConfig,
} from '../utils/jobScheduleUtils';
import { ContentPageLayout } from '../components/Shared';

const CATEGORY_COLORS: Record<string, 'primary' | 'secondary' | 'warning' | 'default'> = {
  metrics: 'primary',
  maintenance: 'warning',
  migration: 'secondary',
};

const JobsAdminPage: React.FC = () => {
  const [jobs, setJobs] = useState<JobDefinition[]>([]);
  const [schedulerEnabled, setSchedulerEnabled] = useState(false);
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [runsLoading, setRunsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterJobId, setFilterJobId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [runDialogJob, setRunDialogJob] = useState<JobDefinition | null>(null);
  const [runParams, setRunParams] = useState<Record<string, string>>({});
  const [runDryRun, setRunDryRun] = useState(false);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);

  const [scheduleDialogJob, setScheduleDialogJob] = useState<JobDefinition | null>(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>(defaultScheduleConfig());
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const [detailRun, setDetailRun] = useState<JobRun | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listJobs();
      setJobs(res.data.jobs);
      setSchedulerEnabled(res.data.schedulerEnabled);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await listJobRuns({
        jobId: filterJobId || undefined,
        status: filterStatus || undefined,
        limit: 30,
      });
      setRuns(res.data.runs);
      setRunsTotal(res.data.total);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [filterJobId, filterStatus]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const openRunDialog = (job: JobDefinition) => {
    const defaults: Record<string, string> = {};
    for (const p of job.paramSchema) {
      if (p.default != null) defaults[p.key] = String(p.default);
    }
    setRunParams(defaults);
    setRunDryRun(false);
    setRunDialogJob(job);
  };

  const handleRunJob = async () => {
    if (!runDialogJob) return;
    setRunningJobId(runDialogJob.jobId);
    try {
      const params: Record<string, unknown> = {};
      for (const p of runDialogJob.paramSchema) {
        const raw = runParams[p.key];
        if (raw === undefined || raw === '') continue;
        params[p.key] = p.type === 'number' ? Number(raw) : raw;
      }
      await triggerJob(runDialogJob.jobId, { params, dryRun: runDryRun });
      setRunDialogJob(null);
      await Promise.all([loadJobs(), loadRuns()]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Job run failed');
    } finally {
      setRunningJobId(null);
    }
  };

  const openScheduleDialog = (job: JobDefinition) => {
    setScheduleDialogJob(job);
    setScheduleEnabled(job.schedule.enabled);
    setScheduleConfig(
      defaultScheduleConfig(
        job.schedule.cronExpression || job.defaultCron,
        normalizeJobTimezone(job.schedule.timezone)
      )
    );
  };

  const handleSaveSchedule = async () => {
    if (!scheduleDialogJob) return;
    if (scheduleEnabled) {
      const err = validateScheduleConfig(scheduleConfig);
      if (err) {
        setError(err);
        return;
      }
    }
    setScheduleSaving(true);
    try {
      const cronExpression = scheduleEnabled
        ? buildCronFromConfig(scheduleConfig)
        : null;
      await updateJobSchedule(scheduleDialogJob.jobId, {
        enabled: scheduleEnabled,
        cronExpression,
        timezone: scheduleConfig.timezone,
      });
      setScheduleDialogJob(null);
      await loadJobs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const openRunDetail = async (runId: string) => {
    setDetailLoading(true);
    setDetailRun(null);
    try {
      const res = await getJobRun(runId);
      setDetailRun(res.data);
    } catch {
      setError('Failed to load run details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">
            Jobs & batches
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule maintenance tasks, run backfills, and review execution history.
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            size="small"
            label={schedulerEnabled ? 'Scheduler on' : 'Scheduler off'}
            color={schedulerEnabled ? 'success' : 'default'}
            variant="outlined"
          />
          <Button startIcon={<RefreshIcon />} onClick={() => { loadJobs(); loadRuns(); }} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {!schedulerEnabled ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          In-process scheduler is disabled. Set <code>JOBS_SCHEDULER_ENABLED=true</code> on the server to run cron schedules automatically. Manual runs always work.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} mb={3}>
          {jobs.map((job) => (
            <Grid item xs={12} md={6} key={job.jobId}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {job.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {job.jobId}
                    </Typography>
                    <Chip
                      size="small"
                      label={job.category}
                      color={CATEGORY_COLORS[job.category] || 'default'}
                      sx={{ mr: 1, mb: 1 }}
                    />
                    {job.isRunning ? (
                      <Chip size="small" label="Running" color="info" sx={{ mb: 1 }} />
                    ) : null}
                  </Box>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="Schedule">
                      <IconButton size="small" onClick={() => openScheduleDialog(job)}>
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Run now">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={job.isRunning || runningJobId === job.jobId}
                          onClick={() => openRunDialog(job)}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                  {job.description}
                </Typography>

                <Typography variant="caption" color="text.secondary" display="block">
                  {describeSchedule(
                    job.schedule.enabled,
                    job.schedule.cronExpression,
                    normalizeJobTimezone(job.schedule.timezone)
                  )}
                </Typography>

                {job.lastRun ? (
                  <Box mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={job.lastRun.status}
                      color={statusColor(job.lastRun.status)}
                    />
                    {job.lastRun.dryRun ? <Chip size="small" label="dry run" variant="outlined" /> : null}
                    <Typography variant="caption" color="text.secondary">
                      {job.lastRun.finishedAt
                        ? new Date(job.lastRun.finishedAt).toLocaleString()
                        : '—'}
                      {job.lastRun.durationMs != null
                        ? ` · ${formatDurationMs(job.lastRun.durationMs)}`
                        : ''}
                    </Typography>
                    {job.lastRun.errorMessage ? (
                      <Typography variant="caption" color="error">
                        {job.lastRun.errorMessage}
                      </Typography>
                    ) : null}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    No runs yet
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Run history
        </Typography>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Job"
            value={filterJobId}
            onChange={(e) => setFilterJobId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All jobs</MenuItem>
            {jobs.map((j) => (
              <MenuItem key={j.jobId} value={j.jobId}>
                {j.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="running">Running</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary" alignSelf="center">
            {runsTotal} total
          </Typography>
        </Box>

        {runsLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No runs match filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => (
                  <TableRow key={run._id} hover>
                    <TableCell>{run.jobId}</TableCell>
                    <TableCell>
                      <Chip size="small" label={run.status} color={statusColor(run.status)} />
                      {run.dryRun ? (
                        <Chip size="small" label="dry" variant="outlined" sx={{ ml: 0.5 }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{run.trigger}</TableCell>
                    <TableCell>
                      {run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{formatDurationMs(run.durationMs)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openRunDetail(run._id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Run dialog */}
      <Dialog open={Boolean(runDialogJob)} onClose={() => setRunDialogJob(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Run: {runDialogJob?.name}</DialogTitle>
        <DialogContent>
          {runDialogJob?.paramSchema.map((p) => (
            <TextField
              key={p.key}
              fullWidth
              size="small"
              margin="normal"
              label={p.label}
              type={p.type === 'number' ? 'number' : 'text'}
              value={runParams[p.key] ?? ''}
              onChange={(e) => setRunParams((prev) => ({ ...prev, [p.key]: e.target.value }))}
              helperText={p.required ? 'Required' : 'Optional'}
            />
          ))}
          {runDialogJob?.supportsDryRun ? (
            <FormControlLabel
              control={
                <Switch checked={runDryRun} onChange={(e) => setRunDryRun(e.target.checked)} />
              }
              label="Dry run (no writes)"
              sx={{ mt: 1 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogJob(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRunJob}
            disabled={runningJobId != null}
          >
            {runningJobId ? 'Running…' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog
        open={Boolean(scheduleDialogJob)}
        onClose={() => setScheduleDialogJob(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule: {scheduleDialogJob?.name}</DialogTitle>
        <DialogContent>
          <JobScheduleEditor
            enabled={scheduleEnabled}
            onEnabledChange={setScheduleEnabled}
            config={scheduleConfig}
            onChange={setScheduleConfig}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogJob(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSchedule} disabled={scheduleSaving}>
            {scheduleSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run detail dialog */}
      <Dialog
        open={detailLoading || Boolean(detailRun)}
        onClose={() => setDetailRun(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Run details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <CircularProgress size={24} />
          ) : detailRun ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>{detailRun.jobId}</strong> · {detailRun.status} · {detailRun.trigger}
                {detailRun.dryRun ? ' · dry run' : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                {detailRun.startedAt ? new Date(detailRun.startedAt).toLocaleString() : ''}
                {detailRun.durationMs != null ? ` · ${formatDurationMs(detailRun.durationMs)}` : ''}
              </Typography>

              {detailRun.error?.message ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {detailRun.error.message}
                </Alert>
              ) : null}

              {detailRun.result != null ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Result
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                    <Typography
                      component="pre"
                      variant="caption"
                      sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'monospace' }}
                    >
                      {JSON.stringify(detailRun.result, null, 2)}
                    </Typography>
                  </Paper>
                </Box>
              ) : null}

              {detailRun.logs && detailRun.logs.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Logs
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, maxHeight: 280, overflow: 'auto', bgcolor: 'background.default' }}
                  >
                    {detailRun.logs.map((line, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRun(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ContentPageLayout>
  );
};

export default JobsAdminPage;
